/**
 * Header Search — embedded predictive search for the redesigned header
 */

if (!customElements.get('header-search')) {
  customElements.define(
    'header-search',
    class HeaderSearch extends HTMLElement {
      constructor() {
        super();

        this.cachedResults = new Map();
        this.abortController = null;
        this.debounceTimer = null;
        this.debounceDelay = 300;

        this.input = this.querySelector('.header__search-input');
        this.form = this.querySelector('.header__search-form') || this.querySelector('form[id^="HeaderSearchForm"]');
        this.clearBtn = this.querySelector('.clear');
        this.voiceBtn = this.querySelector('.voice');
        this.resultsContainer = this.querySelector('.header__search-results');
        this.trendingDropdown = this.querySelector('.header__trending-dropdown');

        if (!this.input || !this.form) return;

        this._bindEvents();
        this._interceptHeaderSearch();
        this._setupVoice();
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get query() {
        return this.input.value.trim();
      }

      /* ---- Events ---- */

      _bindEvents() {
        this.input.addEventListener('input', () => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => this._onInput(), this.debounceDelay);
        });

        this.input.addEventListener('focus', () => {
          if (this.query.length > 0) {
            this.setAttribute('data-has-query', '');
            this._fetchResults(this.query);
          } else {
            // Show trending dropdown when focused with empty query
            this._showTrending();
          }
        });

        this.clearBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          this._clear();
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.hasAttribute('data-results-open')) {
            this._close();
          }
        });

        document.addEventListener('mousedown', (e) => {
          if (this.hasAttribute('data-results-open') && !this.contains(e.target)) {
            this._close();
          }
        });

        this.addEventListener('click', (e) => {
          const viewAllBtn = e.target.closest('[data-view-all]');
          if (viewAllBtn) {
            e.preventDefault();
            this.form.submit();
          }
        });

        this.form.addEventListener('submit', (e) => {
          if (this.query.length === 0) e.preventDefault();
          // Sync query to hidden form input
          const hiddenQ = this.form.querySelector('input[name="q"]');
          if (hiddenQ) hiddenQ.value = this.query;
        });

        // Handle Enter key on input to submit the form
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (this.query.length > 0) {
              const hiddenQ = this.form.querySelector('input[name="q"]');
              if (hiddenQ) hiddenQ.value = this.query;
              this.form.submit();
            }
          }
        });
      }

      /* ---- Input ---- */

      _onInput() {
        const q = this.query;
        if (q.length === 0) {
          this._clear();
          return;
        }
        this.setAttribute('data-has-query', '');
        this._fetchResults(q);
      }

      /* ---- Fetch ---- */

      _fetchResults(query) {
        const url = this._buildUrl(query);

        if (this.cachedResults.has(url)) {
          this._renderResults(this.cachedResults.get(url));
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.setAttribute('data-loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.text();
          })
          .then((html) => {
            this.cachedResults.set(url, html);
            this._renderResults(html);
            this.removeAttribute('data-loading');
          })
          .catch((err) => {
            if (err.name !== 'AbortError') {
              console.error('[HeaderSearch]', err);
            }
            this.removeAttribute('data-loading');
          });
      }

      _buildUrl(query) {
        const shopUrl = window.theme?.routes?.shop_url || window.location.origin;
        const predictivePath = window.theme?.routes?.predictive_search_url || '/search/suggest';

        const url = new URL(`${shopUrl}${predictivePath}`);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,query,article,page');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', this.sectionId);

        return url.toString();
      }

      _renderResults(html) {
        const targetId = `PredictiveSearchResults-${this.sectionId}`;
        const target = document.getElementById(targetId);
        if (!target) return;

        try {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const newContent = parsed.getElementById(targetId);

          if (newContent) {
            target.innerHTML = newContent.innerHTML;

            const viewMore = target.querySelector('.psb__view-more');
            if (viewMore && this.resultsContainer) {
              const existing = this.resultsContainer.querySelector(':scope > .psb__view-more');
              if (existing) existing.remove();
              this.resultsContainer.appendChild(viewMore);
            }
          } else {
            console.warn('[HeaderSearch] No #' + targetId + ' in response');
            return;
          }
        } catch (err) {
          console.error('[HeaderSearch] parse error:', err);
          return;
        }

        this._hideTrending();
        this.setAttribute('data-results-open', '');
      }

      /* ---- State ---- */

      _showTrending() {
        if (this.trendingDropdown) {
          this.setAttribute('data-trending-open', '');
          this.setAttribute('data-results-open', '');
        }
      }

      _hideTrending() {
        this.removeAttribute('data-trending-open');
      }

      _clear() {
        this.input.value = '';
        this.input.focus();
        this.removeAttribute('data-has-query');
        this.removeAttribute('data-results-open');
        this.removeAttribute('data-loading');
        this._showTrending();
      }

      _close() {
        this.removeAttribute('data-results-open');
        this.removeAttribute('data-trending-open');
        if (this.query.length === 0) {
          this.removeAttribute('data-has-query');
        }
        this.input.blur();
      }

      /* ---- Intercept any legacy search drawer buttons ---- */

      _interceptHeaderSearch() {
        requestAnimationFrame(() => {
          const btns = document.querySelectorAll('.search-drawer-button, [aria-controls="SearchDrawer"]');
          btns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
              if (this.offsetParent !== null) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                this.input.focus();
                const rect = this.getBoundingClientRect();
                if (rect.top < 0 || rect.top > window.innerHeight) {
                  window.scrollTo({ top: this.offsetTop - 80, behavior: 'smooth' });
                }
              }
            }, { capture: true });
          });
        });
      }

      /* ---- Voice search (mic) — mirrors the theme's native 6.0 voice-search ---- */

      _setupVoice() {
        this.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        // No mic button, or the browser can't do speech recognition (e.g. Firefox): leave it hidden.
        if (!this.voiceBtn || !this.SpeechRecognition) return;

        // Safari: if the OS voice service was unavailable earlier this session, stay hidden.
        try {
          if (window.sessionStorage && window.sessionStorage.getItem('petmax:voice-unavailable')) return;
        } catch (e) {}

        this._syncMicPermission();
        this.voiceBtn.addEventListener('click', (e) => this._onVoiceClick(e));
      }

      async _syncMicPermission() {
        const reveal = () => this.setAttribute('data-voice-available', '');
        try {
          const status = await navigator.permissions.query({ name: 'microphone' });
          const sync = () => {
            if (status.state === 'denied' || this._voiceUnavailable) {
              this.removeAttribute('data-voice-available');
            } else {
              reveal();
            }
          };
          sync();
          status.addEventListener('change', sync);
        } catch (e) {
          // Permissions API unsupported — reveal and let the first click prompt for access.
          reveal();
        }
      }

      _onVoiceClick(event) {
        event.preventDefault();

        if (this._listening) {
          this._recognition?.stop();
          return;
        }

        this._recognition = new this.SpeechRecognition();
        this._recognition.lang = document.documentElement.lang || 'en';
        this._recognition.interimResults = false;
        this._recognition.maxAlternatives = 1;

        this._recognition.addEventListener('result', (e) => this._onVoiceResult(e));
        this._recognition.addEventListener('end', (e) => this._onVoiceEnd(e));
        this._recognition.addEventListener('error', (e) => this._onVoiceEnd(e));

        this._listening = true;
        this.voiceBtn.classList.add('listening');
        this.input.focus();

        try {
          this._recognition.start();
        } catch (e) {
          this._onVoiceEnd({ error: 'start-failed' });
        }
      }

      _onVoiceResult(event) {
        const transcript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join('');

        this.input.value = transcript;
        this.input.focus();
        this.setAttribute('data-has-query', '');
        // Run predictive search on the spoken query (reuses the existing debounced input handler).
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      _onVoiceEnd(event) {
        this._listening = false;
        this.voiceBtn?.classList.remove('listening');

        // Safari: OS voice service unavailable — stop offering the mic for this session.
        if (event && event.error === 'service-not-allowed') this._markVoiceUnavailable();
      }

      _markVoiceUnavailable() {
        this._voiceUnavailable = true;
        try {
          if (window.sessionStorage) window.sessionStorage.setItem('petmax:voice-unavailable', 'true');
        } catch (e) {}
        this.removeAttribute('data-voice-available');
      }
    }
  );
}
